import { FunctionComponent, ReactNode } from 'react'

import { Root, Item, Indicator } from '@radix-ui/react-radio-group'

import styled from 'styled-components'

import { baseColors } from '../../primitives/colors'
import { Tick } from '../icons/Tick'
import { Text } from '../Text'

const ItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const ItemContainer = styled.div<{ isChecked: boolean }>`
  display: flex;
  align-items: center;
  flex-grow: 1;
  border: 1px solid ${baseColors.borderBackground};
  background-color: ${({ isChecked }) =>
    isChecked
      ? baseColors.backgroundsPrimarySurface
      : baseColors.backgroundCard};
  color: ${baseColors.contentContent};
  border-radius: 10px;
  cursor: pointer;

  &:hover {
    &:hover {
      background-color: ${({ isChecked }) =>
        isChecked
          ? baseColors.backgroundsPrimarySurface
          : baseColors.statesHoverBackground};
      color: ${({ isChecked }) =>
        isChecked ? baseColors.contentContent : baseColors.backgroundCard};
      button {
        background-color: ${({ isChecked }) =>
          isChecked ? baseColors.contentMuted : baseColors.backgroundCard};
        color: ${({ isChecked }) =>
          isChecked
            ? baseColors.backgroundsPrimarySurface
            : baseColors.statesHoverBackground};
      }
    }
  }

  &:not(:last-of-type) {
    margin-bottom: 16px;
  }
`

const RadioButton = styled(Item)`
  min-width: 20px;
  width: 20px;
  height: 20px;
  border-radius: 100%;
  border: 2px solid ${baseColors.contentMuted};
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 18px;

  &:focus-visible {
    outline: 2px solid ${baseColors.statesFocus};
    outline-offset: 2px;
  }
`

const CheckedIndicator = styled(Indicator)`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${baseColors.contentMuted};
  border-radius: 100%;
  width: 20px;
  height: 20px;
  color: ${baseColors.backgroundsPrimarySurface};
`

const Label = styled(Text)`
  flex-grow: 1;
  cursor: pointer;
  padding: 16px 24px 16px 18px;
`

interface RadioButtonItem {
  id: string
  value: string
  label: ReactNode
}

export interface RadioButtonsProps {
  ariaLabel: string
  items: RadioButtonItem[]
  value?: string
  onValueChange: (value: string) => void
  className?: string
}

export const RadioButtons: FunctionComponent<RadioButtonsProps> = ({
  ariaLabel,
  items,
  value,
  onValueChange,
  className,
}) => (
  <Root
    aria-label={ariaLabel}
    value={value}
    onValueChange={onValueChange}
    loop
    className={className}
  >
    <ItemsContainer>
      {items.map(({ id, value: itemValue, label }) => (
        <ItemContainer key={id} isChecked={itemValue === value}>
          <RadioButton value={itemValue} id={id}>
            <CheckedIndicator>
              <Tick width="13" height="13" />
            </CheckedIndicator>
          </RadioButton>
          <Label
            element="label"
            type="body"
            size="sm"
            fontWeight={500}
            htmlFor={id}
          >
            {label}
          </Label>
        </ItemContainer>
      ))}
    </ItemsContainer>
  </Root>
)
